import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Paper,
  Snackbar,
  Toolbar,
  Typography,
  Stack,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import CancelIcon from "@mui/icons-material/Cancel";

export default function FeeApprovalAdmin() {
  const base = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [approvedPage, setApprovedPage] = useState(0);
  const [rejectedPage, setRejectedPage] = useState(0);

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [notice, setNotice] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [proofOpen, setProofOpen] = useState(false);
  const [proofUrl, setProofUrl] = useState("");
  const [searchText, setSearchText] = useState("");

  const filterRows = (data) => {
    const search = searchText.trim().toLowerCase();

    if (!search) return data;

    return data.filter((row) =>
      [row.student_rollno, row.student_name, row.fee_head_name].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search)
      )
    );
  };

  const handleOpenProof = (path) => {
    if (!path) return;

    const url = /^https?:\/\//i.test(path)
      ? path
      : `${base}${path.startsWith("/") ? "" : "/"}${path}`;

    setProofUrl(url);
    setProofOpen(true);
  };

  const handleCloseProof = () => {
    setProofOpen(false);
    setProofUrl("");
  };

  // Load all approval requests except completed records.
  const load = async (silent = false) => {
    if (!silent) setBusy(true);

    try {
      const response = await axios.get(
        `${base}/api/fee-approvals/${user.client_id}/${user.college_id}`
      );

      const data = Array.isArray(response.data?.data) ? response.data.data : [];

      setRows(
        data
          .filter((item) => String(item.workflow_status || "").toUpperCase() !== "COMPLETED")
          .map((item) => ({
            ...item,
            id: String(item.approve_id),
          }))
      );
    } catch (error) {
      if (!silent) {
        setNotice({
          open: true,
          message: error.response?.data?.message || "Unable to load approval requests.",
          severity: "error",
        });
      }
    } finally {
      if (!silent) setBusy(false);
    }
  };

  useEffect(() => {
    load();

    const refreshTimer = window.setInterval(() => {
      load(true);
    }, 3000);

    return () => window.clearInterval(refreshTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingRows = useMemo(
    () =>
      rows.filter((item) => {
        const status = String(item.workflow_status || "")
          .trim()
          .toUpperCase();

        return status !== "APPROVED" && status !== "REJECTED";
      }),
    [rows]
  );

  const approvedRows = useMemo(
    () => rows.filter((item) => String(item.workflow_status || "").toUpperCase() === "APPROVED"),
    [rows]
  );

  const rejectedRows = useMemo(
    () => rows.filter((item) => String(item.workflow_status || "").toUpperCase() === "REJECTED"),
    [rows]
  );
  const filteredPendingRows = useMemo(() => filterRows(pendingRows), [pendingRows, searchText]);

  const filteredApprovedRows = useMemo(() => filterRows(approvedRows), [approvedRows, searchText]);

  const filteredRejectedRows = useMemo(() => filterRows(rejectedRows), [rejectedRows, searchText]);
  // Confirm, submit, and display the result using SweetAlert2.
  const update = async (action, approvalIds = selected) => {
    if (!approvalIds.length) {
      await Swal.fire({
        icon: "warning",
        title: "No Request Selected",
        text: "Please select at least one approval request.",
        confirmButtonColor: "#1976d2",
      });
      return;
    }

    const isApprove = action === "approve";
    const actionText = isApprove ? "Approve" : "Reject";

    const confirmation = await Swal.fire({
      icon: "question",
      title: `${actionText} Fee Request${approvalIds.length > 1 ? "s" : ""}?`,
      text: isApprove
        ? `Are you sure you want to approve ${approvalIds.length} selected request(s)?`
        : `Are you sure you want to reject ${approvalIds.length} selected request(s)?`,
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: isApprove ? "#2e7d32" : "#d32f2f",
      cancelButtonColor: "#757575",
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    setBusy(true);

    try {
      const response = await axios.patch(
        `${base}/api/fee-approvals/${user.client_id}/${user.college_id}/${action}`,
        {
          approve_ids: approvalIds,
          modified_by: user.user_email || "",
        }
      );

      setSelected([]);

      // Refresh all three grids with the latest statuses.
      await load();

      setNotice({
        open: true,
        message:
          response.data?.message ||
          `Fee request(s) ${isApprove ? "approved" : "rejected"} successfully.`,
        severity: "success",
      });

      await Swal.fire({
        icon: "success",
        title: `${actionText} Successful`,
        text:
          response.data?.message ||
          `The selected fee request(s) have been ${isApprove ? "approved" : "rejected"}.`,
        confirmButtonColor: "#1976d2",
      });
    } catch (error) {
      const message =
        error.response?.data?.message || `Unable to ${action} the selected request(s).`;

      setNotice({
        open: true,
        message,
        severity: "error",
      });

      await Swal.fire({
        icon: "error",
        title: `${actionText} Failed`,
        text: message,
        confirmButtonColor: "#d32f2f",
      });
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 75,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
          return index >= 0 ? index + 1 : "-";
        },
      },
      {
        field: "student_rollno",
        headerName: "Roll No",
        width: 160,
      },
      {
        field: "student_name",
        headerName: "Student Name",
        minWidth: 230,
        flex: 1,
      },
      {
        field: "fee_head_name",
        headerName: "Fee Book",
        minWidth: 300,
        flex: 1,
      },
      {
        field: "payable_details",
        headerName: "Payable Details",
        minWidth: 270,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ py: 1, lineHeight: 1.7 }}>
            <Typography variant="body2">
              <strong>Date:</strong>{" "}
              {params.row.payable_date
                ? new Date(params.row.payable_date).toLocaleDateString("en-IN")
                : "-"}
            </Typography>

            <Typography variant="body2">
              <strong>Fine:</strong> ₹{params.row.fine_amount || 0}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              <strong>Remarks:</strong> {params.row.remarks || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "approval_proof_path",
        headerName: "Proof",
        width: 100,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleOpenProof(value)}
              sx={{
                bgcolor: "#4caf50",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: "#388e3c" },
              }}
            >
              View
            </Button>
          ) : (
            "-"
          ),
      },
      {
        field: "workflow_status",
        headerName: "Status",
        width: 130,
        renderCell: ({ value }) => {
          const status = String(value || "PENDING").toUpperCase();

          return (
            <Chip
              size="medium"
              label={status}
              color={
                status === "APPROVED" ? "success" : status === "REJECTED" ? "error" : "warning"
              }
            />
          );
        },
      },
    ],
    [base]
  );

  const rejectedColumns = useMemo(
    () => [
      ...columns,
      /*  {
        field: "reapprove",
        headerName: "Re-Approval",
        width: 165,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            startIcon={<ReplayIcon />}
            disabled={busy}
            onClick={() => update("approve", [params.row.approve_id])}
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Re-Approve
          </Button>
        ),
      }, */
    ],
    [columns, busy]
  );

  const gridSx = {
    border: 0,
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#e3f2fd",
      fontWeight: 700,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
    },
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
    },
  };

  return (
    <Box p={2} pb={6}>
      <Paper elevation={3} sx={{ p: { xs: 1, sm: 2 } }}>
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2 },
            py: { xs: 2, sm: 1.5 },
            gap: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            flexWrap: "wrap",
          }}
        >
          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              fontSize: { xs: 20, sm: 23, md: 25 },
              textAlign: { xs: "center", md: "left" },
              whiteSpace: { md: "nowrap" },
            }}
          >
            FEE ALLOWED APPROVAL
          </Typography>

          {/* Search */}
          <Box
            sx={{
              width: { xs: "100%", md: 350, lg: 400 },
              flexShrink: 0,
            }}
          >
            <TextField
              fullWidth
              size="medium"
              label="Search Roll No / Name / Fee Book"
              placeholder="Enter Roll No, Name or Fee Book..."
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(0);
                setApprovedPage(0);
                setRejectedPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: { xs: 14, sm: 16 },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: 13, sm: 15 },
                },
              }}
            />
          </Box>

          {/* Approve / Reject */}
          <ButtonGroup
            variant="contained"
            fullWidth
            sx={{
              width: { xs: "100%", md: "auto" },
              display: "flex",
              "& .MuiButton-root": {
                flex: { xs: 1, md: "initial" },
                minWidth: { xs: 0, sm: 110 },
                py: 1.2,
                fontSize: { xs: 13, sm: 14 },
                fontWeight: 700,
                whiteSpace: "nowrap",
              },
            }}
          >
            <Button
              disabled={busy}
              startIcon={<CheckCircleIcon />}
              onClick={() => update("approve")}
              sx={{
                bgcolor: "#2e7d32",
                color: "#fff",
                "&:hover": { bgcolor: "#1b5e20" },
                "&.Mui-disabled": {
                  bgcolor: "#a5d6a7",
                  color: "#fff",
                },
              }}
            >
              Approve
            </Button>

            <Button
              disabled={busy}
              startIcon={<CancelIcon />}
              onClick={() => update("reject")}
              sx={{
                bgcolor: "#d32f2f",
                color: "#fff",
                "&:hover": { bgcolor: "#b71c1c" },
                "&.Mui-disabled": {
                  bgcolor: "#ef9a9a",
                  color: "#fff",
                },
              }}
            >
              Reject
            </Button>
          </ButtonGroup>
        </Toolbar>
        {/* APPROVAL TABS */}
        <Box sx={{ mt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 64,
              borderBottom: 1,
              borderColor: "divider",

              "& .MuiTabs-indicator": {
                height: 5,
                backgroundColor: "#17202a",
              },

              "& .MuiTab-root": {
                minHeight: 64,
                minWidth: { xs: 130, sm: 180 },
                px: { xs: 2, sm: 3 },
                py: 1.5,
                mr: 0.5,
                borderRadius: "8px 8px 0 0",
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: 13, sm: 16 },
                textTransform: "none",
                opacity: 1,
                transition: "all 0.2s ease",
              },

              /* Pending - Light Orange */
              "& .MuiTab-root:nth-of-type(1)": {
                backgroundColor: "#FFE0B2",
                color: "#8D4A00",
                "&:hover": { backgroundColor: "#FFCC80" },
              },

              /* Approved - Light Green */
              "& .MuiTab-root:nth-of-type(2)": {
                backgroundColor: "#C8E6C9",
                color: "#1B5E20",
                "&:hover": { backgroundColor: "#A5D6A7" },
              },

              /* Rejected - Light Red */
              "& .MuiTab-root:nth-of-type(3)": {
                backgroundColor: "#FFCDD2",
                color: "#B71C1C",
                "&:hover": { backgroundColor: "#EF9A9A" },
              },

              "& .MuiTab-root.Mui-selected": {
                color: "#17202A",
                filter: "brightness(0.97)",
                boxShadow: "inset 0 -4px 0 #17202A",
              },

              "& .MuiTabs-scrollButtons": {
                color: "#1976d2",
                width: 32,
              },
            }}
          >
            <Tab label={`Pending (${filteredPendingRows.length})`} />
            <Tab label={`Approved (${filteredApprovedRows.length})`} />
            <Tab label={`Rejected (${filteredRejectedRows.length})`} />
          </Tabs>

          {/* PENDING APPROVALS */}
          {activeTab === 0 && (
            <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Pending Approvals
                </Typography>
                <Chip label={filteredPendingRows.length} color="warning" size="small" />
              </Stack>

              <DataGrid
                autoHeight
                checkboxSelection
                rows={filteredPendingRows}
                columns={columns}
                loading={busy}
                rowHeight={85}
                sx={gridSx}
                selectionModel={selected}
                onSelectionModelChange={setSelected}
                pageSize={pageSize}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                pagination
                disableSelectionOnClick
                isRowSelectable={(params) =>
                  !["APPROVED", "REJECTED", "COMPLETED"].includes(
                    String(params.row.workflow_status || "").toUpperCase()
                  )
                }
              />
            </Box>
          )}

          {/* APPROVED REQUESTS */}
          {activeTab === 1 && (
            <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Approved Requests
                </Typography>
                <Chip label={filteredApprovedRows.length} color="success" size="small" />
              </Stack>

              <DataGrid
                autoHeight
                rows={filteredApprovedRows}
                columns={columns}
                loading={busy}
                rowHeight={85}
                sx={gridSx}
                pageSize={pageSize}
                page={approvedPage}
                onPageChange={setApprovedPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setApprovedPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                pagination
                disableSelectionOnClick
              />
            </Box>
          )}

          {/* REJECTED REQUESTS */}
          {activeTab === 2 && (
            <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Rejected Requests
                </Typography>
                <Chip label={filteredRejectedRows.length} color="error" size="small" />
              </Stack>

              <DataGrid
                autoHeight
                rows={filteredRejectedRows}
                columns={rejectedColumns}
                loading={busy}
                rowHeight={85}
                sx={gridSx}
                pageSize={pageSize}
                page={rejectedPage}
                onPageChange={setRejectedPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setRejectedPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                pagination
                disableSelectionOnClick
              />
            </Box>
          )}
        </Box>

        {/* PROOF PREVIEW DIALOG */}
        <Dialog open={proofOpen} onClose={handleCloseProof} maxWidth="lg" fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Approval Proof
            <IconButton onClick={handleCloseProof} size="medium" sx={{ color: "error.main" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {proofUrl.toLowerCase().split("?")[0].endsWith(".pdf") ? (
              <iframe
                src={proofUrl}
                title="PDF Proof"
                width="100%"
                height="600px"
                style={{ border: "none" }}
              />
            ) : (
              <Box
                component="img"
                src={proofUrl}
                alt="Approval Proof"
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseProof} color="error">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={() => setNotice((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          variant="filled"
          severity={notice.severity}
          onClose={() => setNotice((previous) => ({ ...previous, open: false }))}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
